import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  // NOTE: we intentionally do NOT set `withCredentials: true` globally.
  // The browser blocks cross-origin responses that combine
  // `Access-Control-Allow-Origin: *` with credentials mode `include`,
  // which would prevent anonymous visitors from seeing any listing.
  // Auth is carried via the `Authorization: Bearer` header (localStorage).
  // The backend ALSO sets an httpOnly cookie on login/register/google as a
  // future-proof fallback, but the frontend currently relies on the header,
  // so we don't need to send cookies on every request.
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('roomia_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('roomia_token');
      if (window.location.pathname !== '/auth') {
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

// Listings
export const getListings = (params) => api.get('/listings', { params });
export const getFeaturedListings = (limit = 6) => api.get('/listings/featured', { params: { limit } });
export const getListing = (id) => api.get(`/listings/${id}`);
export const createListing = (data) => api.post('/listings', data);
export const updateListing = (id, data) => api.put(`/listings/${id}`, data);
export const deleteListing = (id) => api.delete(`/listings/${id}`);
export const uploadListingImages = (id, files) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  return api.post(`/listings/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getMyListings = () => api.get('/my-listings');

// Favorites
export const getFavorites = () => api.get('/favorites');
export const getFavoriteIds = () => api.get('/favorites/ids');
export const addToFavorites = (listingId) => api.post(`/favorites/${listingId}`);
export const removeFromFavorites = (listingId) => api.delete(`/favorites/${listingId}`);

// Blog
export const getBlogPosts = (publishedOnly = true) => api.get('/blog/posts', { params: { published_only: publishedOnly } });
export const getBlogPost = (id) => api.get(`/blog/posts/${id}`);
export const createBlogPost = (data) => api.post('/blog/posts', data);
export const updateBlogPost = (id, data) => api.put(`/blog/posts/${id}`, data);
export const deleteBlogPost = (id) => api.delete(`/blog/posts/${id}`);

// Ads
export const getAds = (position) => api.get('/ads', { params: { position } });
export const trackAdClick = (id) => api.get(`/ads/${id}/click`);
export const createAd = (data) => api.post('/admin/ads', data);
export const getAdminAds = () => api.get('/admin/ads');
export const updateAd = (id, data) => api.put(`/admin/ads/${id}`, data);
export const deleteAd = (id) => api.delete(`/admin/ads/${id}`);

// Locations
export const getCountries = () => api.get('/locations/countries');
export const getCities = (countryId) => api.get('/locations/cities', { params: { country_id: countryId } });
export const getNeighborhoods = (cityId) => api.get('/locations/neighborhoods', { params: { city_id: cityId } });
export const searchNeighborhoods = (cityId, query) => api.get('/locations/neighborhoods/search', { params: { city_id: cityId, q: query } });
export const proposeNeighborhood = (data) => api.post('/locations/neighborhoods/propose', data);

// Contact & Partnership
export const sendContactMessage = (data) => api.post('/contact', data);
export const sendPartnershipRequest = (data) => api.post('/partnership', data);

// AI Search
export const aiSearch = (query, language) => api.post('/ai-search', { query, language });

// AI Description Generation
export const generateAIDescription = (data) => api.post('/ai/generate-description', data);

// Notifications
export const getNotifications = (params) => api.get('/notifications', { params });
export const getUnreadNotificationCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const createNotification = (data) => api.post('/admin/notifications', data);
export const getAdminNotifications = () => api.get('/admin/notifications');
export const deleteNotification = (id) => api.delete(`/admin/notifications/${id}`);

// Admin
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminListings = (params) => api.get('/admin/listings', { params });
export const toggleFeatured = (id) => api.put(`/admin/listings/${id}/feature`);
export const verifyListing = (id) => api.put(`/admin/listings/${id}/verify`);
export const approveListing = (id) => api.put(`/admin/listings/${id}/approve`);
export const rejectListing = (id) => api.put(`/admin/listings/${id}/reject`);
export const adminDeleteListing = (id) => api.delete(`/admin/listings/${id}`);
export const getPendingListings = () => api.get('/admin/listings/pending');
export const getAdminUsers = (params) => api.get('/admin/users', { params });
export const getAdminUserDetail = (userId) => api.get(`/admin/users/${userId}`);
export const updateUserRole = (userId, role) => api.put(`/admin/users/${userId}/role`, null, { params: { role } });
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const restoreUser = (userId) => api.post(`/admin/users/${userId}/restore`);
export const getContactMessages = () => api.get('/admin/contact-messages');
export const getPartnershipRequests = () => api.get('/admin/partnership-requests');

// Locations Admin
export const createCountry = (data) => api.post('/admin/locations/countries', data);
export const createCity = (data) => api.post('/admin/locations/cities', data);
export const createNeighborhood = (data) => api.post('/admin/locations/neighborhoods', data);
export const getPendingNeighborhoods = () => api.get('/admin/locations/neighborhoods/pending');
export const approveNeighborhood = (id) => api.put(`/admin/locations/neighborhoods/${id}/approve`);
export const rejectNeighborhood = (id) => api.put(`/admin/locations/neighborhoods/${id}/reject`);
export const deleteCity = (id) => api.delete(`/admin/locations/cities/${id}`);
export const deleteNeighborhood = (id) => api.delete(`/admin/locations/neighborhoods/${id}`);
export const seedLocations = () => api.post('/seed/locations');

// Admin Listing Actions
export const disableListing = (id) => api.put(`/admin/listings/${id}/disable`);
export const requestListingModification = (id, message) => api.put(`/admin/listings/${id}/request-modification`, null, { params: { message } });

// Seed
export const seedData = () => api.post('/seed');

// Admin Export/Backup
export const exportUsersPdf = () => api.get('/admin/export/users-pdf', { responseType: 'blob' });
export const exportFullBackup = () => api.get('/admin/export/full-backup', { responseType: 'blob' });
export const exportImagesZip = () => api.get('/admin/export/images-zip', { responseType: 'blob' });
export const restoreBackup = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/import/restore-backup', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const renameListingImages = (id) => api.put(`/admin/listings/${id}/rename-images`);

// Analytics & Tracking
export const getAnalyticsOverview = (period = '30d') => api.get('/admin/analytics/overview', { params: { period } });
export const exportAnalytics = (format = 'csv') => api.get('/admin/analytics/export', { 
  params: { format },
  responseType: 'blob'
});
export const getUserActivity = (userId) => api.get(`/admin/analytics/user-activity/${userId}`);

// User Stats
export const getUserStats = () => api.get('/user/stats');

// Admin Cleanup & Security
export const runSecurityAudit = () => api.get('/admin/security-audit');
export const cleanupTestData = () => api.delete('/admin/cleanup/test-data');
export const prepareProduction = () => api.post('/admin/prepare-production');

// Tracking (for user actions)
export const trackWhatsAppClick = (listingId) => api.post('/track/whatsapp-click', null, { params: { listing_id: listingId } });
export const trackListingView = (listingId) => api.post('/track/listing-view', null, { params: { listing_id: listingId } });
export const trackSearch = (params) => api.post('/track/search', null, { params });

// Cloudinary video
export const getCloudinarySignature = (listingId) => api.get('/cloudinary/signature', { params: { listing_id: listingId } });
export const attachListingVideo = (listingId, payload) => api.post(`/listings/${listingId}/video`, payload);
export const removeListingVideo = (listingId) => api.delete(`/listings/${listingId}/video`);

export default api;
